import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { generateStarPositions } from '../utils/starPositions';
import { API_URL } from '../config/api';
import { useLocation } from 'react-router-dom';
import { debounce } from 'lodash';

// Create a context
const StarContext = createContext();

// Create a provider component
export const StarProvider = ({ children }) => {
    const [stars, setStars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalWeeks, setTotalWeeks] = useState(0);
    const [randomStars, setRandomStars] = useState([]);
    const [publicStars, setPublicStars] = useState([]);
    const [starPositions, setStarPositions] = useState([]);
    const [maxVisibleStars, setMaxVisibleStars] = useState(30);
    const [unplacedStars, setUnplacedStars] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [starsPerPage] = useState(10);
    const [starQueue, setStarQueue] = useState([]);
    const [isProcessingQueue, setIsProcessingQueue] = useState(false);
    const [isChangingPage, setIsChangingPage] = useState(false);
    const location = useLocation();
    const hasInitializedMessages = useRef(false);

    // Define getCurrentPageStars first since other functions depend on it
    const getCurrentPageStars = useCallback(() => {
        if (!publicStars?.length) return [];
        const startIndex = (currentPage - 1) * starsPerPage;
        const stars = publicStars.slice(startIndex, startIndex + starsPerPage);
        //console.log('Getting current page stars:', {
        //    currentPage,
        //    startIndex,
        //    starsPerPage,
        //    totalStars: publicStars.length,
        //    starsInPage: stars.length,
        //    firstStarInPage: stars[0],
        //    lastStarInPage: stars[stars.length - 1]
        //});
        return stars;
    }, [publicStars, currentPage, starsPerPage]);

    const hasMorePages = useCallback(() => {
        if (!publicStars?.length) return false;
        
        // Calculate total stars and current position
        const startIndex = (currentPage - 1) * starsPerPage;
        const totalStars = publicStars.length;
        
        // We always have more pages since we're looping
        const hasMore = true;
        //console.log('Checking for more pages:', {
        //    currentPage,
        //    startIndex,
        //    totalStars,
        //    starsPerPage,
        //    hasMore,
        //    unplacedStarsCount: unplacedStars.length
        //});
        
        return hasMore;
    }, [currentPage, starsPerPage, publicStars, unplacedStars]);

    const loadNextPage = useCallback(() => {
        if (!loading && !isChangingPage) {
            //console.log('Starting to load next page:', {
            //    currentPage,
            //    totalStars: publicStars.length,
            //    starsPerPage
            //});
            
            // Start the transition
            setIsChangingPage(true);
            
            // Clear positions for fade out animation
            setStarPositions([]);
            
            // Wait for fade out to complete
            setTimeout(() => {
                // Calculate the next page number first
                const nextPage = currentPage + 1;
                let startIndex;
                
                // If we've reached the end, loop back to the beginning
                if (nextPage > Math.ceil(publicStars.length / starsPerPage)) {
                    startIndex = 0;
                } else {
                    startIndex = (nextPage - 1) * starsPerPage;
                }
                
                const nextPageStars = publicStars.slice(startIndex, startIndex + starsPerPage);
                const starsToPlace = [...nextPageStars, ...unplacedStars];
                
                //console.log('Loading next page stars:', {
                //    currentPage,
                //    nextPage,
                //    startIndex,
                //    starsPerPage,
                //    starsToPlaceCount: starsToPlace.length,
                //    nextPageStarsCount: nextPageStars.length,
                //    unplacedStarsCount: unplacedStars.length,
                //    firstStar: nextPageStars[0],
                //    lastStar: nextPageStars[nextPageStars.length - 1]
                //});
                
                if (starsToPlace.length > 0) {
                    // Generate positions with optimized parameters
                    const { positions, unplacedIndices } = generateStarPositions(
                        starsToPlace.length,
                        starsToPlace,
                        maxVisibleStars,
                        [] // Pass empty array for existingPositions since we cleared them
                    );

                    // Create array of positioned stars with validation
                    const newPositions = [];
                    const newUnplacedStars = [];
                    
                    positions.forEach((pos, index) => {
                        if (!unplacedIndices.includes(index)) {
                            // Validate position before adding
                            const star = starsToPlace[index];
                            if (star && pos.x !== undefined && pos.y !== undefined) {
                                newPositions.push({
                                    ...pos,
                                    star: star
                                });
                            } else {
                                newUnplacedStars.push(star);
                            }
                        } else {
                            newUnplacedStars.push(starsToPlace[index]);
                        }
                    });

                    // Store unplaced stars for next attempt
                    setUnplacedStars(newUnplacedStars);

                    // Update positions with new stars
                    //console.log('Setting positions:', {
                    //    attempted: starsToPlace.length,
                    //    placed: newPositions.length,
                    //    unplaced: newUnplacedStars.length,
                    //    viewportWidth: window.innerWidth,
                    //    viewportHeight: window.innerHeight,
                    //    currentPage,
                    //    nextPage,
                    //    startIndex,
                    //    firstStar: newPositions[0]?.star,
                    //    lastStar: newPositions[newPositions.length - 1]?.star
                    //});
                    
                    setStarPositions(newPositions);
                }
                
                // Update the page number, resetting to 1 if we've reached the end
                setCurrentPage(nextPage > Math.ceil(publicStars.length / starsPerPage) ? 1 : nextPage);
                
                // Reset changing page state after positions are updated
                setTimeout(() => {
                    setIsChangingPage(false);
                }, 800);
            }, 800);
        }
    }, [loading, isChangingPage, currentPage, starsPerPage, publicStars, unplacedStars, maxVisibleStars]);

    // Update positions for current page with debouncing
    const updateStarPositions = useCallback(() => {
        if (!loading && publicStars.length > 0) {
            const currentStars = getCurrentPageStars();
            if (currentStars.length === 0) return;

            // Generate positions with optimized parameters
            const { positions, unplacedIndices } = generateStarPositions(
                currentStars.length,
                currentStars,
                maxVisibleStars,
                [] // Pass empty array for initial placement
            );

            // Create array of positioned stars with validation
            const newPositions = [];
            const newUnplacedStars = [];
            
            positions.forEach((pos, index) => {
                if (!unplacedIndices.includes(index)) {
                    // Validate position before adding
                    const star = currentStars[index];
                    if (star && pos.x !== undefined && pos.y !== undefined) {
                        newPositions.push({
                            ...pos,
                            star: star
                        });
                    } else {
                        newUnplacedStars.push(star);
                    }
                } else {
                    newUnplacedStars.push(currentStars[index]);
                }
            });

            // Store unplaced stars for next attempt
            setUnplacedStars(newUnplacedStars);
            setStarPositions(newPositions);

            //console.log('Initial placement:', {
            //    attempted: currentStars.length,
            //    placed: newPositions.length,
            //    unplaced: newUnplacedStars.length,
            //    viewportWidth: window.innerWidth,
            //    viewportHeight: window.innerHeight
            //});
        }
    }, [loading, publicStars, getCurrentPageStars, maxVisibleStars]);

    // Move API functions to the top
    const fetchTotalWeeks = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/stats/total-weeks`);
            if (!response.ok) throw new Error('Failed to fetch total weeks');
            const data = await response.json();
            setTotalWeeks(data.totalWeeks);
        } catch (err) {
            console.error('Error fetching total weeks:', err);
        }
    }, []);

    const fetchRandomStars = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/stars/random`);
            if (!response.ok) throw new Error('Failed to fetch random stars');
            const stars = await response.json();
            setRandomStars(stars);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPublicStars = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/stars/public`);
            if (!response.ok) throw new Error('Failed to fetch public stars');
            const stars = await response.json();
            //console.log('Fetched public stars:', {
            //    totalStars: stars.length,
            //    firstStar: stars[0],
            //    lastStar: stars[stars.length - 1]
            //});
            setPublicStars(stars);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const findStar = useCallback(async (usid) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/stars/${usid}`);
            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Star not found');
                }
                throw new Error('Failed to fetch star');
            }
            const star = await response.json();
            return star;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const generateReadableId = (name, dob, placeOfBirth) => {
        const formattedName = name.trim().replace(/\s+/g, '-').toLowerCase();
        const formattedDob = new Date(dob).toISOString().split('T')[0];
        const formattedPlace = placeOfBirth.trim().replace(/\s+/g, '-').toLowerCase();
        return `${formattedName}-${formattedDob}-${formattedPlace}`;
    };

    const addStar = async (starData) => {
        const usid = generateReadableId(starData.name, starData.dob, starData.pob);
        const starWithId = { ...starData, usid };
        //console.log('Sending data:', starWithId);
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/stars`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(starWithId),
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown error occurred' }));
                console.error('Server error response:', errorData);
                throw new Error(`Failed to save star: ${errorData.message || response.statusText}`);
            }
            
            const savedStar = await response.json();
            setStars(prevStars => [...prevStars, savedStar]);
            return savedStar;
        } catch (err) {
            console.error('Error in addStar:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Fetch initial data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await Promise.all([
                    fetchTotalWeeks(),
                    fetchRandomStars(),
                    fetchPublicStars()
                ]);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Handle route changes
    useEffect(() => {
        if (location.pathname === '/messages') {
            // Only reset if we haven't initialized messages yet
            if (!hasInitializedMessages.current) {
                //console.log('First time loading messages, initializing state');
                setCurrentPage(1);
                setStarPositions([]);
                setUnplacedStars([]); // Clear unplaced stars on route change
                updateStarPositions();
                hasInitializedMessages.current = true;
            } else {
                //console.log('Already initialized messages, skipping state reset');
            }
        } else {
            // Reset the initialization flag when leaving messages
            hasInitializedMessages.current = false;
        }
    }, [location.pathname, updateStarPositions]);

    // Handle window resize with optimized debouncing
    useEffect(() => {
        const handleResize = debounce(() => {
            // Only update positions if we're not in the middle of a transition
            if (!isChangingPage) {
                setStarPositions([]);
                updateStarPositions();
            }
        }, 250);

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateStarPositions, isChangingPage]);

    return (
        <StarContext.Provider value={{ 
            stars, 
            randomStars,
            publicStars,
            starPositions,
            setStarPositions,
            maxVisibleStars,
            setMaxVisibleStars,
            currentPage,
            setCurrentPage,
            starsPerPage,
            starQueue,
            hasMorePages,
            loadNextPage,
            getCurrentPageStars,
            isChangingPage,
            addStar, 
            findStar, 
            loading, 
            error,
            setError,
            totalWeeks,
            fetchTotalWeeks,
            fetchRandomStars,
            fetchPublicStars,
            updateStarPositions
        }}>
            {children}
        </StarContext.Provider>
    );
};

// Custom hook to use the StarContext
export const useStars = () => {
    const context = useContext(StarContext);
    if (!context) {
        throw new Error('useStars must be used within a StarProvider');
    }
    return context;
}; 